import React, { useState, useEffect } from 'react';
import './Toast.css';

/**
 * Toast component for temporary notifications
 * Based on ArbiWin design system
 *
 * @param {Object} props - Toast properties
 * @param {React.ReactNode} props.children - Toast content
 * @param {string} props.variant - Toast variant: 'success', 'error', 'warning', 'info'
 * @param {string} props.title - Toast title
 * @param {number} props.duration - Auto-dismiss duration in milliseconds (0 = no auto-dismiss)
 * @param {boolean} props.dismissible - Whether toast can be manually dismissed
 * @param {Function} props.onDismiss - Dismiss handler function
 * @param {React.ReactNode} props.icon - Custom icon component
 * @param {boolean} props.showIcon - Whether to show default icon
 * @param {string} props.position - Toast position: 'top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} Toast component
 */
const Toast = function Toast({
  children,
  variant = 'info',
  title,
  duration = 5000,
  dismissible = true,
  onDismiss,
  icon,
  showIcon = true,
  position = 'top-right',
  className = '',
  testId
}) {

  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = function() {
    setIsLeaving(true);
    setTimeout(function() {
      setIsVisible(false);
      if (onDismiss) {
        onDismiss();
      }
    }, 200); // Match CSS animation duration
  };

  useEffect(function() {
    if (duration > 0) {
      const timer = setTimeout(function() {
        handleDismiss();
      }, duration);

      return function() {
        clearTimeout(timer);
      };
    }
  }, [duration, handleDismiss]);

  const toastClasses = [
    'arbiwin-toast',
    'arbiwin-toast--' + variant,
    'arbiwin-toast--' + position,
    isLeaving ? 'arbiwin-toast--leaving' : '',
    className
  ].filter(Boolean).join(' ');

  const getDefaultIcon = function() {
    const icons = {
      success: (
        <svg className="arbiwin-toast__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="arbiwin-toast__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="arbiwin-toast__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      info: (
        <svg className="arbiwin-toast__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      )
    };
    return icons[variant] || icons.info;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={toastClasses}
      role="alert"
      aria-live="polite"
      data-testid={testId || 'arbiwin-toast'}
    >
      {(showIcon || icon) && (
        <div className="arbiwin-toast__icon-wrapper">
          {icon || getDefaultIcon()}
        </div>
      )}

      <div className="arbiwin-toast__content">
        {title && (
          <div className="arbiwin-toast__title">
            {title}
          </div>
        )}
        {children && (
          <div className="arbiwin-toast__message">
            {children}
          </div>
        )}
      </div>

      {dismissible && (
        <button
          className="arbiwin-toast__dismiss"
          onClick={handleDismiss}
          aria-label="Затвори известието"
          type="button"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      {duration > 0 && (
        <div className="arbiwin-toast__progress">
          <div
            className="arbiwin-toast__progress-bar"
            style={{ animationDuration: duration + 'ms' }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * ToastContainer component for managing multiple toasts
 *
 * @param {Object} props - ToastContainer properties
 * @param {Array} props.toasts - Array of toast objects
 * @param {string} props.position - Container position
 * @param {number} props.maxToasts - Maximum number of toasts to show
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ToastContainer component
 */
const ToastContainer = function ToastContainer({
  toasts = [],
  position = 'top-right',
  maxToasts = 5,
  className = ''
}) {

  const containerClasses = [
    'arbiwin-toast-container',
    'arbiwin-toast-container--' + position,
    className
  ].filter(Boolean).join(' ');

  const visibleToasts = toasts.slice(0, maxToasts);

  if (visibleToasts.length === 0) {
    return null;
  }

  return (
    <div className={containerClasses}>
      {visibleToasts.map(function(toast) {
        return (
          <Toast
            key={toast.id}
            variant={toast.variant}
            title={toast.title}
            duration={toast.duration}
            dismissible={toast.dismissible}
            onDismiss={toast.onDismiss}
            icon={toast.icon}
            showIcon={toast.showIcon}
            position={position}
          >
            {toast.message}
          </Toast>
        );
      })}
    </div>
  );
};

/**
 * useToast hook for managing toast notifications
 *
 * @returns {Object} Toast management functions
 */
const useToast = function() {
  const [toasts, setToasts] = useState([]);

  const removeToast = function(id) {
    setToasts(function(prevToasts) {
      return prevToasts.filter(function(toast) {
        return toast.id !== id;
      });
    });
  };

  const addToast = function(toast) {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      variant: 'info',
      duration: 5000,
      dismissible: true,
      showIcon: true,
      ...toast,
      onDismiss: function() {
        removeToast(id);
        if (toast.onDismiss) {
          toast.onDismiss();
        }
      }
    };

    setToasts(function(prevToasts) {
      return [...prevToasts, newToast];
    });

    return id;
  };

  const removeAllToasts = function() {
    setToasts([]);
  };

  const showSuccess = function(message, options = {}) {
    return addToast({
      variant: 'success',
      title: options.title || 'Успех!',
      message,
      ...options
    });
  };

  const showError = function(message, options = {}) {
    return addToast({
      variant: 'error',
      title: options.title || 'Грешка!',
      message,
      duration: 0, // Don't auto-dismiss errors
      ...options
    });
  };

  const showWarning = function(message, options = {}) {
    return addToast({
      variant: 'warning',
      title: options.title || 'Внимание!',
      message,
      ...options
    });
  };

  const showInfo = function(message, options = {}) {
    return addToast({
      variant: 'info',
      title: options.title || 'Информация',
      message,
      ...options
    });
  };

  return {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};

Toast.displayName = 'Toast';
ToastContainer.displayName = 'ToastContainer';

export default Toast;
export { ToastContainer, useToast };
